<script setup lang="ts">
import { onMounted, ref } from "vue";

import { api, type Experiment, type ExperimentsDocument } from "./api";

const props = defineProps<{ siteId: string }>();
const document = ref<ExperimentsDocument | null>(null);
const canEdit = ref(false);
const loading = ref(true);
const saving = ref(false);
type ExperimentImage = Experiment["images"][number];
const uploading = ref<ExperimentImage | null>(null);
const error = ref("");
const notice = ref("");
const experimentKeys = new WeakMap<Experiment, string>();
const imageKeys = new WeakMap<ExperimentImage, string>();
let nextExperimentKey = 0;
let nextImageKey = 0;

function experimentKey(experiment: Experiment): string {
  if (experiment.id) return experiment.id;
  let key = experimentKeys.get(experiment);
  if (!key) {
    nextExperimentKey += 1;
    key = `experiment-${nextExperimentKey}`;
    experimentKeys.set(experiment, key);
  }
  return key;
}

function imageKey(image: ExperimentImage): string {
  let key = imageKeys.get(image);
  if (!key) {
    nextImageKey += 1;
    key = `experiment-image-${nextImageKey}`;
    imageKeys.set(image, key);
  }
  return key;
}

function newExperiment(): Experiment {
  return {
    title: "New experiment",
    label: "In the lab",
    paragraphs: [],
    images: [],
    imageUrl: "",
    imageAlt: "",
    externalUrl: "",
    presentation: "standard",
    isPublished: true,
  };
}

function addExperiment() {
  document.value?.experiments.push(newExperiment());
}

function move(index: number, direction: -1 | 1) {
  if (!document.value) return;
  const destination = index + direction;
  if (destination < 0 || destination >= document.value.experiments.length) return;
  const [item] = document.value.experiments.splice(index, 1);
  if (item) document.value.experiments.splice(destination, 0, item);
}

function addParagraph(experiment: Experiment) {
  experiment.paragraphs.push("");
}

function addImage(experiment: Experiment) {
  experiment.images.push({ url: "", alt: "" });
}

function moveImage(experiment: Experiment, index: number, direction: -1 | 1) {
  const destination = index + direction;
  if (destination < 0 || destination >= experiment.images.length) return;
  const [image] = experiment.images.splice(index, 1);
  if (image) experiment.images.splice(destination, 0, image);
}

async function uploadImage(event: Event, image: ExperimentImage) {
  const input = event.currentTarget as HTMLInputElement;
  const file = input.files?.[0] ?? null;
  input.value = "";
  if (!file || !canEdit.value) return;
  uploading.value = image; error.value = ""; notice.value = "";
  try {
    const response = await api.uploadExperimentImage(props.siteId, file);
    image.url = response.image.url;
    notice.value = "Image uploaded. Save experiments to apply it to The Lab.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to upload the image";
  } finally {
    if (uploading.value === image) uploading.value = null;
  }
}

async function save() {
  if (!document.value || !canEdit.value) return;
  saving.value = true; error.value = ""; notice.value = "";
  try {
    document.value = (await api.updateExperiments(props.siteId, document.value)).experiments;
    notice.value = "Experiments saved and available in The Lab.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save experiments";
  } finally { saving.value = false; }
}

onMounted(async () => {
  try {
    const response = await api.experiments(props.siteId);
    document.value = response.experiments;
    canEdit.value = response.canEdit;
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load experiments";
  } finally { loading.value = false; }
});
</script>

<template>
  <section class="experiments-card">
    <div class="module-heading">
      <div><p class="eyebrow">Portfolio module</p><h2>Experiments</h2><p>Cards for prototypes, screenshots, unfinished ideas and quiet hints.</p></div>
      <div class="heading-actions"><button type="button" :disabled="!canEdit" @click="addExperiment">Add experiment</button><button type="button" :disabled="saving || !canEdit" @click="save">{{ saving ? "Saving..." : "Save experiments" }}</button></div>
    </div>
    <p v-if="error" class="message error" role="alert">{{ error }}</p>
    <p v-if="notice" class="message success" role="status">{{ notice }}</p>
    <p v-if="loading" class="loading">Loading experiments...</p>

    <div v-else-if="document" class="experiment-grid">
      <article v-for="(experiment, index) in document.experiments" :key="experimentKey(experiment)" class="experiment-editor">
        <header>
          <div><p class="eyebrow">Card {{ index + 1 }}</p><h3>{{ experiment.title || "Untitled experiment" }}</h3></div>
          <div class="row-actions"><button type="button" :disabled="!canEdit || index === 0" @click="move(index, -1)">↑</button><button type="button" :disabled="!canEdit || index === document.experiments.length - 1" @click="move(index, 1)">↓</button><button class="remove" type="button" :disabled="!canEdit" @click="document.experiments.splice(index, 1)">Remove</button></div>
        </header>

        <div class="experiment-fields">
          <label>Title<input v-model="experiment.title" :disabled="!canEdit" maxlength="160"></label>
          <label>Small label<input v-model="experiment.label" :disabled="!canEdit" maxlength="100" placeholder="Prototype / Visual test / Incoming"></label>
          <label>Card style<select v-model="experiment.presentation" :disabled="!canEdit"><option value="standard">Standard card</option><option value="image">Image-led</option><option value="teaser">Teaser / hint</option></select></label>
          <label class="published"><input v-model="experiment.isPublished" :disabled="!canEdit" type="checkbox"> Published in The Lab</label>
        </div>

        <div class="gallery-heading"><div><h4>Image gallery</h4><p>Images keep this order in the horizontally scrollable Lab card.</p></div><button type="button" :disabled="!canEdit || experiment.images.length >= 12" @click="addImage(experiment)">Add image</button></div>
        <div v-if="experiment.images.length" class="image-gallery-editor">
          <article v-for="(image, imageIndex) in experiment.images" :key="imageKey(image)" class="image-editor">
            <div class="image-preview"><img v-if="image.url.startsWith('/') || image.url.startsWith('http')" :src="image.url" :alt="image.alt || experiment.title"><span v-else>Image {{ imageIndex + 1 }}</span></div>
            <div class="image-fields">
              <p class="image-number">Image {{ imageIndex + 1 }} of {{ experiment.images.length }}</p>
              <label>Image URL or theme path<input v-model="image.url" :disabled="!canEdit" maxlength="500"></label>
              <label>Image description<input v-model="image.alt" :disabled="!canEdit" maxlength="300"></label>
              <div class="image-actions">
                <label class="upload-button" :class="{ disabled: !canEdit || uploading === image }">{{ uploading === image ? "Uploading..." : "Upload image" }}<input class="file-input" type="file" accept="image/jpeg,image/png,image/webp" :disabled="!canEdit || uploading !== null" @change="uploadImage($event, image)"></label>
                <button type="button" :disabled="!canEdit || imageIndex === 0" @click="moveImage(experiment, imageIndex, -1)">←</button><button type="button" :disabled="!canEdit || imageIndex === experiment.images.length - 1" @click="moveImage(experiment, imageIndex, 1)">→</button>
                <button class="remove" type="button" :disabled="!canEdit" @click="experiment.images.splice(imageIndex, 1)">Remove</button>
              </div>
            </div>
          </article>
        </div>

        <div class="paragraph-heading"><div><h4>Paragraphs</h4><p>Optional—image-led and teaser cards can stay brief.</p></div><button type="button" :disabled="!canEdit" @click="addParagraph(experiment)">Add paragraph</button></div>
        <div v-for="(_paragraph, paragraphIndex) in experiment.paragraphs" :key="paragraphIndex" class="paragraph-row"><textarea v-model="experiment.paragraphs[paragraphIndex]" :disabled="!canEdit" rows="4" maxlength="3000" /><button class="remove" type="button" :disabled="!canEdit" @click="experiment.paragraphs.splice(paragraphIndex, 1)">Remove</button></div>
        <label>Optional external link<input v-model="experiment.externalUrl" :disabled="!canEdit" type="url" maxlength="500" placeholder="https://..."></label>
      </article>

      <div v-if="!document.experiments.length" class="empty"><p>No experiments yet.</p><button type="button" :disabled="!canEdit" @click="addExperiment">Add the first Lab card</button></div>
    </div>
  </section>
</template>

<style scoped>
.experiments-card { display:grid; gap:1.25rem; padding:1.5rem; border:1px solid #d9ddd8; border-radius:1rem; background:#fff; }.module-heading,.experiment-editor>header,.paragraph-heading,.gallery-heading { display:flex; align-items:center; justify-content:space-between; gap:1rem; }.module-heading h2,.experiment-editor h3,.paragraph-heading h4,.gallery-heading h4 { margin:0; }.module-heading p:last-child,.paragraph-heading p,.gallery-heading p { margin:.35rem 0 0; color:#6d7972; }.eyebrow { margin:0 0 .25rem; color:#6b7b72; font-size:.68rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; }.heading-actions,.row-actions { display:flex; gap:.45rem; }.heading-actions button:first-child,.row-actions button,.paragraph-heading button,.gallery-heading button,.image-actions button { color:#315342; background:#e8eeea; }.row-actions button { min-width:2.5rem; padding:.6rem; }.row-actions .remove,.remove { color:#91382f; background:#fae9e7; }.experiment-grid { display:grid; gap:1rem; }.experiment-editor { display:grid; gap:1rem; padding:1.2rem; border:1px solid #dce2dc; border-radius:.8rem; background:#fafbf9; }.experiment-fields { display:grid; grid-template-columns:1fr 1fr; gap:.8rem; }.published { display:flex; align-items:center; align-self:end; min-height:2.8rem; gap:.5rem; }.published input { width:auto; }.image-gallery-editor { display:flex; padding:.2rem 0 .7rem; overflow-x:auto; gap:.8rem; scroll-snap-type:x proximity; }.image-editor { display:grid; min-width:min(42rem,90%); grid-template-columns:minmax(12rem,.65fr) 1fr; gap:1rem; padding:1rem; border:1px solid #dce2dc; border-radius:.7rem; background:#fff; scroll-snap-align:start; }.image-preview { display:grid; min-height:12rem; overflow:hidden; place-items:center; border-radius:.6rem; color:#758078; background:#edf1ee; }.image-preview img { width:100%; height:100%; object-fit:cover; }.image-fields { display:grid; align-content:start; gap:.8rem; }.image-number { margin:0; color:#6d7972; font-size:.75rem; font-weight:800; text-transform:uppercase; }.image-actions { display:flex; align-items:center; flex-wrap:wrap; gap:.4rem; }.image-actions button { padding:.65rem .75rem; }.image-actions .remove { color:#91382f; background:#fae9e7; }.upload-button { display:inline-flex; width:max-content; padding:.7rem .9rem; align-items:center; justify-content:center; border-radius:.65rem; color:#fff; background:#285c47; cursor:pointer; font-weight:700; }.upload-button.disabled { cursor:wait; opacity:.65; }.file-input { position:absolute; width:1px; height:1px; overflow:hidden; clip:rect(0,0,0,0); }.paragraph-row { display:grid; grid-template-columns:1fr auto; align-items:start; gap:.6rem; }.paragraph-row textarea { width:100%; padding:.75rem; resize:vertical; border:1px solid #cbd5ce; border-radius:.65rem; background:#fff; font:inherit; }.message { margin:0; padding:.85rem 1rem; border-radius:.65rem; }.message.error { color:#86251f; background:#fee9e7; }.message.success { color:#295b40; background:#e2f1e7; }.empty,.loading { padding:2rem; text-align:center; }
@media(max-width:700px){.module-heading,.experiment-editor>header,.paragraph-heading,.gallery-heading{align-items:stretch;flex-direction:column}.heading-actions,.row-actions{flex-wrap:wrap}.experiment-fields,.image-editor,.paragraph-row{grid-template-columns:1fr}.image-editor{min-width:92%}.upload-button{width:100%}}
</style>
